import React, { Component } from 'react';
import { computed, observable, toJS } from 'mobx';
import { inject, observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';

import { Button, Modal } from 'react-bootstrap';

import ClientsClass from '../../../clients/ClientsClass';
import { AppConstants } from '../../../constants';
import { ILoanCollateralCase, ILoanCollateralCaseUpdate } from '../../../interfaces';
import { Card, EditableCard, FormModal } from '../../../lib/mighty-fields';
import { SessionStoreClass } from '../../../stores';
import SmartBool from '../../../utils/SmartBool';
import { Icon } from '../../common';

import CaseUpdatesTable from './CaseUpdatesTable';
import { CASE_FIELD_SETS, CASE_UPDATE_FIELD_SETS } from './FieldSets';

const { PERMISSIONS } = AppConstants;

interface IProps {
  collateralCase: ILoanCollateralCase;
  onClose: () => void;
  // The list behind the modal shows the case and the dates of its updates, both of which anything
  // saved here changes, so it is re-read rather than left to go stale
  onSave: () => Promise<void>;
}

interface IInjected extends IProps {
  Clients: ClientsClass;
  SessionStore: SessionStoreClass;
}

/**
 * A single collateral case: its own details, editable in place, and the feed of what each side has
 * reported on it.
 */
@inject('Clients', 'SessionStore')
@autoBindMethods
@observer
class CaseDetailsModal extends Component<IProps> {
  @observable private collateralCase: ILoanCollateralCase;
  @observable private isLoadingUpdates = true;
  @observable private showAddUpdateModal = new SmartBool();
  @observable private updates: ILoanCollateralCaseUpdate[] = [];

  public constructor (props: IProps) {
    super(props);

    // The row the table clicked through carries every field the case has, so the cards open filled
    // in rather than reading it again. Only the updates are missing.
    this.collateralCase = props.collateralCase;
  }

  public componentDidMount () {
    this.fetchUpdates();
  }

  private get injected () {
    return this.props as IInjected;
  }

  /**
   * The plain objects react-table and the cards want, rather than the observables mobx made of the
   * responses. Cloned here rather than at the point of use so each keeps its identity between
   * renders -- see the same note on TabPrimaryCases, which this modal re-renders alongside.
   */
  @computed
  private get updateData () {
    return toJS(this.updates);
  }

  @computed
  private get caseModel () {
    return toJS(this.collateralCase);
  }

  // Not @action: a mobx 3 action does not extend across an await, so the assignments below it would
  // run outside the action anyway.
  private async fetchUpdates () {
    const { Clients } = this.injected;

    this.isLoadingUpdates = true;

    try {
      // Every update on the case rather than a page of them, as the tab reads its cases
      this.updates = await Clients.loanCollateralCaseUpdates.listAll({
        params: { case: this.collateralCase.id },
      });
    }
    catch (error) {
      // Transport has already toasted the failure; swallowing it here avoids a second toast.
      // tslint:disable-next-line no-console
      console.error('Error fetching the case updates:', error);
    }
    finally {
      this.isLoadingUpdates = false;
    }
  }

  private async onSaveCase (data: object) {
    const { Clients } = this.injected;

    // FormManager maps a 400 back onto the form fields, so let the error through rather than
    // swallowing it here. Transport has already toasted anything else.
    this.collateralCase = await Clients.loanCollateralCases.update(this.collateralCase.id, data);

    await this.props.onSave();
  }

  private async onAddUpdate (data: object) {
    const { Clients } = this.injected;

    await Clients.loanCollateralCaseUpdates.create({ ...data, case: this.collateralCase.id });

    await this.fetchUpdates();
    // The list behind the modal dates each case by its last update from either side
    await this.props.onSave();
  }

  private renderCaseInfo () {
    const { SessionStore } = this.injected
      , cardProps = {
        fieldSets: CASE_FIELD_SETS,
        model: this.caseModel,
        title: 'Case Info',
      };

    // The endpoint rejects a PATCH from a user without the change permission, so a view-only user
    // gets the card without the edit pencil rather than a modal that cannot save.
    if (!SessionStore.userHasPermission(PERMISSIONS.CHANGE_LOAN_COLLATERAL_CASE)) {
      return <Card {...cardProps} />;
    }

    return <EditableCard {...cardProps} onSave={this.onSaveCase} />;
  }

  private renderAddUpdateButton () {
    const { SessionStore } = this.injected;

    // Creating is rejected without the add permission, so only offer it to users who have it
    if (!SessionStore.userHasPermission(PERMISSIONS.ADD_LOAN_COLLATERAL_CASE_UPDATE)) { return null; }

    return (
      <Button bsStyle='link' className='btn-add-case-update' onClick={this.showAddUpdateModal.setTrue}>
        <Icon type='plus-square-o' /> Add Update
      </Button>
    );
  }

  public render () {
    const { onClose } = this.props;

    return (
      <Modal show className='right side-modal modal-collateral-case' onHide={onClose}>
        <Modal.Header closeButton>
          <Modal.Title>Case Details</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {this.renderCaseInfo()}

          <CaseUpdatesTable
            isLoading={this.isLoadingUpdates}
            newButton={this.renderAddUpdateButton()}
            updates={this.updateData}
          />

          {/* Opened over this modal, so it carries FormModal's own .over backdrop rather than a
              side modal of its own */}
          {this.showAddUpdateModal.isTrue && (
            <FormModal
              className='modal-collateral-case-update'
              fieldSets={CASE_UPDATE_FIELD_SETS}
              onClose={this.showAddUpdateModal.setFalse}
              onSave={this.onAddUpdate}
              saveText='Add Update'
              title='Add Update'
            />
          )}
        </Modal.Body>
      </Modal>
    );
  }
}

export default CaseDetailsModal;
